import {
  configureStore,
  combineReducers,
  ThunkAction,
  Action,
  AnyAction,
} from "@reduxjs/toolkit";
import DashboardSlice from "../customers/Dashboard/DashboardSlice";
import CommonSlice from "./common/CommonSlice";
import userAuthSlice from "../Login/userAuthSlice";
import formsSlice from "../admin/Forms/formsSlice";
import featuresSlice from "../admin/Features/featuresSlice";
import uiSettingsSlice from "../admin/UiSettings/uiSettingsSlice";
import PolicyDetailsSlice from "./user/policyDetails/policyDetailsSlice";
import lookupSlice from "../admin/Lookup/lookupSlice";
import claimsSlice from "./user/claims/claimsSlice";
import adminAuthSlice from "../admin/Login/adminAuthSlice";
import driverSlice from "./user/driver/driverSlice";
import vehicleSlice from "./user/vehicle/vehicleSlice";
import policyChangeSlice from "./user/policyChange/policyChangeSlice";
import bopPolicyChangeSlice from "./user/policyChange/BOPPolicyChangeSlice";
import foPolicyChangeSlice from "./user/policyChange/FOPolicyChangeSlice";
import tasksSlice from "./user/tasks/tasksSlice";
import ProfileSlice from "./user/profile/profileSlice";
import insureAssistSlice from "./user/insureAssist/insureAssistSlice";
import agentAuthSlice from "../Agents/Login/agentAuthSlice";
import AgentPoliciesListingSlice from "../Agents/PolicyListing/PoliciesListingSlice";
import AgentPolicyMetricsSlice from "../Agents/PolicyListing/PolicyMetrics/PolicyMetricsSlice";
import AddTaskSlice from "../Agents/AddTaskForm/AddTaskSlice";
import AgentTasksSlice from "../Agents/MyTasks/MyTasksSlice";
import secretKeysSlice from "../admin/SecretKeys/secretKeysSlice";
import userManagementSlice from "../admin/UserManagement/UserManagementSlice";
import loginActivitySlice from "../admin/LoginActivity/loginActivitySlice";
import lienholderSlice from "../customers/PolicyDetails/policyTabComponents/LienHolders/LienholderSlice";
import agentGlobalSearchSlice from "../Agents/AgentSearchPopper/AgentSearchSlice";
import cmsSlice from "../admin/CMS/CMSSlice";
import communicationTemplatesSlice from "../admin//CommunicationTemplates/CommunicationTemplatesSlice";
import conversationAnalysisSlice from "../admin/ConversationAnalysis/ConversatonAnalysisSlice";
import agentDetailsSlice from "../customers/Dashboard/Agents/AgentDetailsSlice";
import customerSearchSlice from "../Agents/CustomerListing/CustomerListingSlice";
import insureAssistPerformanceMetricsSlice from "../admin/InsureAssistPerformance/InsureAssistPerformanceSlice";
import customerMetricsSlice from "../Agents/CustomerDetails/CustomerDetailsSlice";
import customerPoliciesSlice from "../Agents/CustomerDetailsList/CustomerDetailsListSlice";
import underwriterAIStorySlice from "../Agents/UnderWriter/UnderwriterSlice";
import addNoteSlice from "../Agents/Notes/AddNotesSlice";
import openTaksSlice from "../customers/PolicyDetails/PolicyDetailsHeader/BasicInfoCardQuickActions/BasicInfoCardSlice";
import promptManagementSlice from "../admin/PromptManagement/PromptManagementSlice";
import adminPasswordConfigSlice from "../admin/PasswordConfiguration/passwordConfigurationSlice";
import adminRolesSlice from "../admin/RoleAndPermission/roleAndPermissionSlice";
import customerAnalyticsSlice from "../admin/CustomerAnalytics/CustomerAnalyticsSlice";
import chatUIReducer from "./common/chatUISlice";

const appReducer = combineReducers({
  common: CommonSlice,
  dashboard: DashboardSlice,
  chatUI: chatUIReducer,
  policyDetails: PolicyDetailsSlice,
  policyChange: policyChangeSlice,
  bopPolicyChange: bopPolicyChangeSlice,
  foPolicyChange: foPolicyChangeSlice,
  driver: driverSlice,
  tasks: tasksSlice,
  vehicle: vehicleSlice,
  profile: ProfileSlice,
  userAuth: userAuthSlice,
  forms: formsSlice,
  features: featuresSlice,
  uiSettings: uiSettingsSlice,
  lookup: lookupSlice,
  claims: claimsSlice,
  adminAuth: adminAuthSlice,
  insureAssist: insureAssistSlice,
  agentAuth: agentAuthSlice,
  agentPolicies: AgentPoliciesListingSlice,
  agentPolicyMetrics: AgentPolicyMetricsSlice,
  addTask: AddTaskSlice,
  agentTasks: AgentTasksSlice,
  secretKeys: secretKeysSlice,
  userManagement: userManagementSlice,
  lienholder: lienholderSlice,
  globalSearch: agentGlobalSearchSlice,
  loginActivity: loginActivitySlice,
  contentManagement: cmsSlice,
  conversationAnalysis: conversationAnalysisSlice,
  agentDetails: agentDetailsSlice,
  customerSearch: customerSearchSlice,
  insureAssistPeformanceMetrics: insureAssistPerformanceMetricsSlice,
  customerMetrics: customerMetricsSlice,
  customerPolicies: customerPoliciesSlice,
  agentAddNotes: addNoteSlice,
  communicationTemplates: communicationTemplatesSlice,
  openTaks: openTaksSlice,
  promptManagment: promptManagementSlice,
  adminPasswordConfig: adminPasswordConfigSlice,
  underwriterAIStory: underwriterAIStorySlice,
  adminRoles: adminRolesSlice,
  customerAnalytics: customerAnalyticsSlice,
});

const reducerProxy = (state: any, action: AnyAction) => {
  if (
    action.type === "adminAuth/adminLogout/fulfilled" ||
    action.type === "agentAuth/agentLogout/fulfilled" ||
    // Customer logout is implemented as a createAsyncThunk: "userAuth/logout/*"
    action.type === "logout" ||
    action.type.startsWith("userAuth/logout/")
  ) {
    // Preserve the uiSettings state
    const { uiSettings, features } = state || {};
    // Reset everything else, but inject back uiSettings
    const newState = appReducer(undefined, action);
    return {
      ...newState,
      uiSettings,
      features,
      // re-insert the preserved uiSettings slice
    };
  }

  return appReducer(state, action);
};

export const store = configureStore({
  reducer: reducerProxy,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: false,
    }),
});

export type AppDispatch = typeof store.dispatch;
export type RootState = ReturnType<typeof store.getState>;
export type AppThunk<ReturnType = void> = ThunkAction<
  ReturnType,
  RootState,
  unknown,
  Action<string>
>;
